import type { ReactNode } from 'react';
import { cn } from './cn';

export interface PanelProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Remove the body padding, for panels that hold a full-bleed table. */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

export function Panel({ title, description, actions, flush, className, bodyClassName, children }: PanelProps) {
  return (
    <section
      className={cn(
        'rounded-[--radius-panel] border border-border bg-surface shadow-card overflow-hidden',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-text">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn(flush ? '' : 'p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'warning' | 'critical' | 'success';
  to?: string;
}

const STAT_TONES: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'text-text',
  warning: 'text-warning-tone',
  critical: 'text-critical-tone',
  success: 'text-success-tone',
};

/**
 * A single headline number.
 *
 * The label sits above the value: someone scanning a row of these reads the
 * labels first to find the one they want, then the number.
 */
export function StatTile({ label, value, hint, icon, tone = 'default' }: StatTileProps) {
  // A description list, so the label and the number are programmatically
  // associated rather than merely sitting next to each other.
  return (
    <dl className="m-0 rounded-[--radius-panel] border border-border bg-surface p-3.5 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <dt className="text-[11px] font-medium tracking-wide text-text-subtle uppercase">{label}</dt>
        {icon && <span className="text-text-subtle" aria-hidden>{icon}</span>}
      </div>
      <dd className={cn('m-0 mt-1.5 text-2xl leading-none font-semibold tabular', STAT_TONES[tone])}>{value}</dd>
      {hint && <dd className="m-0 mt-1.5 text-xs text-text-muted">{hint}</dd>}
    </dl>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb && <div className="mb-1 text-xs text-text-subtle">{breadcrumb}</div>}
        <h1 className="truncate text-xl font-semibold text-text">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
